"use client"

import { useForm, usePage } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { InputBase, TextField } from "@/components/ui/input"
import { Label } from "@/components/ui/input"
import { SectionFooter } from "@/components/ui/section-footer"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionLabel } from "@/components/ui/section-label"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsEmailPath } from "@/routes"

interface EmailPageProps {
  user: { id: number; email: string; verified: boolean }
  [key: string]: unknown
}

export default function Email() {
  const { user } = usePage<EmailPageProps>().props
  const { data, setData, patch, processing, errors, reset } = useForm({
    email: user.email,
    password_challenge: "",
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(settingsEmailPath(), {
      onSuccess: () => reset("password_challenge"),
    })
  }

  return (
    <SettingsLayout>
      <form className="flex flex-col gap-6 px-4 lg:px-8" onSubmit={submit}>
        <SectionHeader.Root>
          <SectionHeader.Group>
            <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
              <SectionHeader.Heading>Email address</SectionHeader.Heading>
              <SectionHeader.Subheading>
                Update your email address.
              </SectionHeader.Subheading>
            </div>

            <SectionHeader.Actions>
              <Button
                type="submit"
                color="primary"
                size="md"
                isDisabled={processing}
              >
                Save
              </Button>
            </SectionHeader.Actions>
          </SectionHeader.Group>
        </SectionHeader.Root>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              isRequired
              size="sm"
              title="Email address"
              className="max-lg:hidden"
            />

            <TextField
              isRequired
              name="email"
              type="email"
              value={data.email}
              onChange={(v) => setData("email", v)}
              isInvalid={!!errors.email}
              errorMessage={errors.email}
            >
              <Label className="lg:hidden">Email address</Label>
              <InputBase size="md" />
            </TextField>
          </div>

          <hr className="bg-border-secondary h-px w-full border-none" />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              isRequired
              size="sm"
              title="Current password"
              description="To change your email, you must verify your current password."
              className="max-lg:hidden"
            />

            <TextField
              isRequired
              name="password_challenge"
              value={data.password_challenge}
              onChange={(v) => setData("password_challenge", v)}
              isInvalid={!!errors.password_challenge}
              errorMessage={errors.password_challenge}
            >
              <Label className="lg:hidden">Current password</Label>
              <InputBase type="password" size="md" />
            </TextField>
          </div>
        </div>

        <SectionFooter.Root>
          <SectionFooter.Actions>
            <Button
              type="submit"
              color="primary"
              size="md"
              isDisabled={processing}
            >
              Save
            </Button>
          </SectionFooter.Actions>
        </SectionFooter.Root>
      </form>
    </SettingsLayout>
  )
}
